import { useState, type ButtonHTMLAttributes, type CSSProperties } from "react";
import { useEmbedTokens, useReducedMotion, motionSafe } from "../theme";
import { EmbedSpinner } from "./EmbedSpinner";

type Variant = "primary" | "secondary" | "ghost" | "danger";

interface Props extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "style"> {
  variant?: Variant;
  size?: "md" | "sm";
  loading?: boolean;
  fullWidth?: boolean;
}

export function EmbedButton({ variant = "primary", size = "md", loading = false, fullWidth = false, type = "button", disabled, onMouseEnter, onMouseLeave, children, ...rest }: Props) {
  const t = useEmbedTokens();
  const reduced = useReducedMotion();
  const [hovered, setHovered] = useState(false);
  const isDisabled = disabled || loading;
  const base: CSSProperties = {
    fontFamily: "inherit", fontSize: size === "sm" ? t.fontSize.xs : t.fontSize.sm, fontWeight: t.fontWeight.medium,
    // Compact only where a mouse is the pointer: on a coarse pointer every size
    // is a full touch target.
    height: size === "sm" && t.pointer === "fine" ? Math.max(t.size.control - 8, 30) : t.buttonHeight, padding: size === "sm" ? "0 12px" : "0 18px",
    borderRadius: t.borderRadiusControl, border: "1px solid transparent", cursor: isDisabled ? "not-allowed" : "pointer",
    display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
    width: fullWidth ? "100%" : undefined, transition: motionSafe(reduced, "background .12s,border-color .12s,color .12s"),
  };
  const byVariant: Record<Variant, CSSProperties> = {
    // onAction, not white: the DS dark on-action is near-black (white on the
    // dark action colour is about 3:1).
    primary: { background: t.colors.primary, color: t.colors.onAction },
    secondary: { background: t.colors.surface, color: t.colors.text, borderColor: t.colors.border },
    // The DS quiet button: text colour, no fill until hovered.
    ghost: { background: "transparent", color: t.colors.text },
    // The DS danger button is filled.
    danger: { background: t.colors.error, color: t.colors.onDanger },
  };
  const hoverByVariant: Record<Variant, CSSProperties> = {
    primary: { background: t.colors.primaryHover },
    secondary: { background: t.colors.surfaceHover },
    ghost: { background: t.colors.surfaceHover },
    danger: { background: t.colors.dangerHover },
  };
  const disabledStyle: CSSProperties = { background: t.colors.disabledBg, color: t.colors.disabled, borderColor: "transparent" };
  const variantStyle = isDisabled ? disabledStyle : { ...byVariant[variant], ...(hovered ? hoverByVariant[variant] : {}) };
  return (
    <button {...rest} type={type} disabled={isDisabled}
      onMouseEnter={(e) => { setHovered(true); onMouseEnter?.(e); }}
      onMouseLeave={(e) => { setHovered(false); onMouseLeave?.(e); }}
      style={{ ...base, ...variantStyle }}>
      {/* TD-576 (1), Task 12: a loading button paints the DISABLED surface whatever its variant,
          so the spinner is the plain accent head, never `onColor` (onAction on that surface read
          white-on-light-grey in light and near-black-on-near-black in dark). */}
      {loading && <EmbedSpinner size={16} />}
      {children}
    </button>
  );
}
